import { User } from '../../../types';
import { userCanAccessCommunity, userCanAccessLessons, isCommunityCourseType, isConversasCourseType } from '../../../utils/enrollmentAccess';

type MenuLink = {
  type: 'link';
  path: string;
  label: string;
  icon: string;
};

type SubItem = {
  path: string;
  label: string;
  icon?: string;
  unreadCount?: number;
};

type MenuSection = {
  type: 'section';
  id: string;
  label: string;
  icon: string;
  isExpanded: boolean;
  subItems: SubItem[];
  disabled?: boolean;
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export type MenuItem = MenuLink | MenuSection;

export async function getMenuItems(
  expandedSections: string[],
  courseId: string,
  user: User | null,
): Promise<MenuItem[]> {
  const token = localStorage.getItem('accessToken');

  const canCommunity = userCanAccessCommunity(user, courseId);
  const canLessons = userCanAccessLessons(user, courseId);
  const isCommunityCourse = isCommunityCourseType(user, courseId);
  const isConversasCourse = isConversasCourseType(user, courseId);

  let unlockedModules: SubItem[] = [];
  let communityModuleLinks: MenuLink[] = [];
  let unreadCounts: Record<string, number> = {};

  if (canCommunity) {
    try {
      const unreadRes = await fetch(
        `${API_BASE_URL}/api/post-categories/unread-posts?courseId=${courseId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (unreadRes.ok) {
        const data = await unreadRes.json();
        unreadCounts = data.unreadPosts.reduce(
          (acc: Record<string, number>, item: any) => {
            acc[item.categoryName] = item.unreadCount;
            return acc;
          },
          {},
        );
      }
    } catch (err) {
      console.warn('⚠️ Falha ao buscar unread-posts, exibindo menu sem contadores', err);
    }
  }

  if (canLessons) {
    try {
      const res = await fetch(`${API_BASE_URL}/api/courses/${courseId}/modules`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        const course = await res.json();
        const modules = course.response;

        const modulesWithLessons = await Promise.all(
          modules.map(async (mod: any, index: number) => {
            try {
              const lessonsRes = await fetch(
                `${API_BASE_URL}/api/courses/${courseId}/modules/${mod.id}/lessons`,
                {
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                },
              );

              if (lessonsRes.ok) {
                const lessons = await lessonsRes.json();
                const hasPublishedLesson = lessons.response.some(
                  (lesson: any) => lesson.isUnlocked,
                );

                if (hasPublishedLesson) {
                  return isCommunityCourse
                    ? ({
                        type: 'link',
                        path: `/aluna/${courseId}/aulas/${mod.id}`,
                        label: mod.title,
                        icon: 'video',
                      } as MenuLink)
                    : ({
                        path: `/aluna/${courseId}/aulas/capitulo/${mod.id}?position=${index}`,
                        label: mod.title,
                      } as SubItem);
                }
              }
            } catch (err) {
              console.warn(`⚠️ Falha ao buscar lessons do módulo ${mod.id}`, err);
            }

            return null;
          }),
        );

        if (isCommunityCourse) {
          communityModuleLinks = modulesWithLessons.filter(Boolean) as MenuLink[];
        } else {
          unlockedModules = modulesWithLessons.filter(Boolean) as SubItem[];
        }
      }
    } catch (err) {
      console.warn('⚠️ Falha ao buscar /modules, exibindo menu sem aulas', err);
    }
  }

  const aulasMenuItems: MenuItem[] = isCommunityCourse
    ? communityModuleLinks
    : [
        {
          type: 'section',
          id: 'aulas',
          label: 'Aulas',
          icon: 'book',
          isExpanded: expandedSections.includes('aulas'),
          subItems: unlockedModules,
          disabled: unlockedModules.length === 0,
        },
      ];

  // Lessons-only access (archived course): simplified menu
  if (canLessons && !canCommunity) {
    return [
      {
        type: 'link',
        path: `/meus-cursos`,
        label: 'Meus cursos',
        icon: 'mycourses',
      },
      ...aulasMenuItems,
      {
        type: 'link',
        path: `/aluna/${courseId}/avisos`,
        label: 'Avisos',
        icon: 'megaphone',
      },
      ...(isConversasCourse
        ? [
            {
              type: 'link',
              path: `/aluna/${courseId}/configuracoes`,
              label: 'Configurações',
              icon: 'settings',
            } as MenuLink,
          ]
        : []),
    ];
  }

  // No access at all: minimal menu
  if (!canLessons && !canCommunity) {
    return [
      {
        type: 'link',
        path: `/meus-cursos`,
        label: 'Meus cursos',
        icon: 'mycourses',
      },
      {
        type: 'link',
        path: `/aluna/${courseId}/avisos`,
        label: 'Avisos',
        icon: 'megaphone',
      },
      {
        type: 'link',
        path: `/aluna/${courseId}/configuracoes`,
        label: 'Configurações',
        icon: 'settings',
      },
    ];
  }

  // Full access
  return [
    {
      type: 'link',
      path: `/aluna/${courseId}`,
      label: 'Início',
      icon: 'home',
    },
    {
      type: 'link',
      path: `/meus-cursos`,
      label: 'Meus cursos',
      icon: 'mycourses',
    },
    {
      type: 'section',
      id: 'comece',
      label: 'Comece por Aqui',
      icon: 'activity',
      isExpanded: expandedSections.includes('comece'),
      subItems: [
        { path: `/aluna/${courseId}/comece/boas-vindas`, label: 'Boas-vindas' },
        { path: `/aluna/${courseId}/comece/apresentacao`, label: 'Apresentação' },
      ],
    },
    ...aulasMenuItems,
    {
      type: 'section',
      id: 'debates',
      label: 'Debates',
      icon: 'message-circle',
      isExpanded: expandedSections.includes('debates'),
      subItems: [
        { path: `/aluna/${courseId}/debates/capitulos`, label: 'Capítulos', icon: '📖', unreadCount: unreadCounts['Capítulos'] ?? 0 },
        { path: `/aluna/${courseId}/debates/girlsplaining`, label: 'GirlsPlaining', icon: '👩‍💻', unreadCount: unreadCounts['GirlsPlaining'] ?? 0 },
        { path: `/aluna/${courseId}/debates/sala-de-espera`, label: 'Sala de espera', icon: '🛋️', unreadCount: unreadCounts['Sala de espera'] ?? 0 },
        { path: `/aluna/${courseId}/debates/indicacoes`, label: 'Indicações', icon: '📝', unreadCount: unreadCounts['Indicações'] ?? 0 },
        { path: `/aluna/${courseId}/debates/relacionamento`, label: 'Relacionamento', icon: '💕', unreadCount: unreadCounts['Relacionamento'] ?? 0 },
        { path: `/aluna/${courseId}/debates/sonhos`, label: 'Sonhos', icon: '✨', unreadCount: unreadCounts['Sonhos'] ?? 0 },
        { path: `/aluna/${courseId}/debates/trabalho`, label: 'Trabalho', icon: '💼', unreadCount: unreadCounts['Trabalho'] ?? 0 },
        { path: `/aluna/${courseId}/debates/desabafo`, label: 'Desabafos', icon: '🗨️', unreadCount: unreadCounts['Desabafo'] ?? 0 },
        { path: `/aluna/${courseId}/debates/assuntos-gerais`, label: 'Assuntos Gerais', icon: '💡', unreadCount: unreadCounts['Assuntos Gerais'] ?? 0 },
      ],
    },
    {
      type: 'link',
      path: `/aluna/${courseId}/shop`,
      label: 'Shop',
      icon: 'shopping-bag',
    },
    {
      type: 'link',
      path: `/aluna/${courseId}/avisos`,
      label: 'Avisos',
      icon: 'megaphone',
    },
    {
      type: 'link',
      path: `/aluna/${courseId}/configuracoes`,
      label: 'Configurações',
      icon: 'settings',
    },
  ];
}
